<template>
  <div class="ats-reporte">

    <!-- ── Encabezado ──────────────────────────────────────────────────── -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="ats-titulo mb-0">
        <i class="bi bi-file-earmark-ruled me-2"></i>ATS — Anexo Transaccional Simplificado
      </h5>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span v-if="reporte" class="ats-periodo-badge">
          {{ nombreMes(reporte.periodo.mes) }} {{ reporte.periodo.anio }}
        </span>
        <button
          v-if="reporte && !cargando"
          class="btn btn-sm btn-outline-success"
          @click="exportarXlsx"
          :disabled="exportando"
          title="Descargar Excel con todos los módulos"
        >
          <span v-if="exportando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-file-earmark-excel me-1"></i>Exportar Excel
        </button>
      </div>
    </div>

    <!-- ── Panel de Filtros (selector de período) ─────────────────────── -->
    <div class="ats-panel mb-3">
      <div class="ats-panel__cuerpo">
        <div class="row g-2 align-items-end">
          <div class="col-sm-4 col-md-3">
            <label class="form-label form-label-sm mb-1">Mes</label>
            <select class="form-select form-select-sm ats-input" v-model.number="filtro.mes" :disabled="cargando">
              <option v-for="(n, i) in mesesNombres" :key="i+1" :value="i+1">{{ n }}</option>
            </select>
          </div>
          <div class="col-sm-4 col-md-2">
            <label class="form-label form-label-sm mb-1">Año</label>
            <select class="form-select form-select-sm ats-input" v-model.number="filtro.anio" :disabled="cargando">
              <option v-for="a in aniosDisponibles" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div class="col-auto">
            <button class="btn btn-sm btn-primary" @click="cargar" :disabled="cargando">
              <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="bi bi-search me-1"></i>{{ cargando ? 'Cargando…' : 'Consultar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Error ──────────────────────────────────────────────────────── -->
    <div v-if="error" class="ats-alerta ats-alerta--error d-flex align-items-center gap-2 mb-3">
      <i class="bi bi-x-circle-fill"></i><span>{{ error }}</span>
    </div>

    <!-- ── Spinner ────────────────────────────────────────────────────── -->
    <div v-if="cargando" class="ats-estado-vacio">
      <span class="spinner-border spinner-border-sm me-2"></span>Consultando período…
    </div>

    <template v-if="reporte && !cargando">

      <!-- ── KPI Cards resumen ─────────────────────────────────────────── -->
      <div class="row g-3 mb-3">
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--primary-main)">{{ reporte.resumen.ventas.cantidad }}</div>
            <div class="ats-kpi__etiqueta">Facturas de venta</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--accent-success)">{{ fmtMon(reporte.resumen.ventas.base_imponible) }}</div>
            <div class="ats-kpi__etiqueta">Base ventas</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.iva_ventas) }}</div>
            <div class="ats-kpi__etiqueta">IVA ventas</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--primary-main)">{{ reporte.resumen.compras.cantidad }}</div>
            <div class="ats-kpi__etiqueta">Facturas de compra</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--accent-success)">{{ fmtMon(reporte.resumen.compras.base_imponible) }}</div>
            <div class="ats-kpi__etiqueta">Base compras</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div class="ats-kpi__valor" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.iva_compras) }}</div>
            <div class="ats-kpi__etiqueta">IVA compras</div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="ats-kpi">
            <div
              class="ats-kpi__valor"
              :style="reporte.resumen.diferencia_iva > 0 ? 'color:var(--accent-danger)' : 'color:var(--accent-success)'"
            >{{ fmtMon(Math.abs(reporte.resumen.diferencia_iva)) }}</div>
            <div class="ats-kpi__etiqueta">
              {{ reporte.resumen.diferencia_iva > 0 ? 'IVA por pagar' : reporte.resumen.diferencia_iva < 0 ? 'Crédito tributario' : 'Sin diferencia IVA' }}
            </div>
          </div>
        </div>
      </div>

      <!-- ── Pestañas ──────────────────────────────────────────────────── -->
      <div class="ats-panel">
        <div class="ats-panel__cabecera">
          <div class="d-flex gap-0 flex-wrap">
            <button
              v-for="tab in pestanas"
              :key="tab.id"
              :class="['ats-tab', tabActivo === tab.id ? 'ats-tab--activo' : '']"
              @click="tabActivo = tab.id"
            >
              <i :class="['bi', tab.icono, 'me-1']"></i>{{ tab.etiqueta }}
              <span class="ats-tab__conteo">{{ tab.conteo }}</span>
            </button>
          </div>
        </div>

        <div class="ats-panel__cuerpo p-0">

          <!-- ── Ventas ─────────────────────────────────────────────── -->
          <div v-show="tabActivo === 'ventas'" class="table-responsive">
            <table class="ats-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>N.° Comprobante</th>
                  <th>Identificación</th>
                  <th>Razón Social</th>
                  <th>Forma Pago</th>
                  <th class="text-end">Base Imponible</th>
                  <th class="text-end">IVA Cobrado</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!reporte.ventas.length">
                  <td colspan="9" class="ats-vacio">Sin facturas de venta en este período</td>
                </tr>
                <tr v-for="(f, i) in reporte.ventas" :key="f.id">
                  <td class="ats-num-col">{{ i + 1 }}</td>
                  <td>{{ f.fecha_emision }}</td>
                  <td class="ats-mono">{{ f.numero_comprobante }}</td>
                  <td>{{ f.identificacion }}</td>
                  <td>{{ f.razon_social }}</td>
                  <td class="text-center">{{ fmtFormaPago(f.forma_pago) }}</td>
                  <td class="text-end">{{ fmtMon(f.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(f.iva_cobrado) }}</td>
                  <td class="text-end fw-semibold">{{ fmtMon(f.total) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="reporte.ventas.length">
                <tr>
                  <td colspan="6">Total ({{ reporte.resumen.ventas.cantidad }} facturas)</td>
                  <td class="text-end">{{ fmtMon(reporte.resumen.ventas.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.iva_ventas) }}</td>
                  <td class="text-end fw-bold">{{ fmtMon(reporte.resumen.ventas.total) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Compras ────────────────────────────────────────────── -->
          <div v-show="tabActivo === 'compras'" class="table-responsive">
            <table class="ats-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>N.° Factura Proveedor</th>
                  <th>Identificación</th>
                  <th>Razón Social</th>
                  <th class="text-end">Base Imponible</th>
                  <th class="text-end">IVA Pagado</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!reporte.compras.length">
                  <td colspan="8" class="ats-vacio">Sin compras registradas en este período</td>
                </tr>
                <tr v-for="(c, i) in reporte.compras" :key="c.id">
                  <td class="ats-num-col">{{ i + 1 }}</td>
                  <td>{{ c.fecha_emision }}</td>
                  <td class="ats-mono">{{ c.numero_factura }}</td>
                  <td>{{ c.identificacion }}</td>
                  <td>{{ c.razon_social }}</td>
                  <td class="text-end">{{ fmtMon(c.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(c.iva_pagado) }}</td>
                  <td class="text-end fw-semibold">{{ fmtMon(c.total) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="reporte.compras.length">
                <tr>
                  <td colspan="5">Total ({{ reporte.resumen.compras.cantidad }} compras)</td>
                  <td class="text-end">{{ fmtMon(reporte.resumen.compras.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.iva_compras) }}</td>
                  <td class="text-end fw-bold">{{ fmtMon(reporte.resumen.compras.total) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Notas de crédito ───────────────────────────────────── -->
          <div v-show="tabActivo === 'notas_credito'" class="table-responsive">
            <table class="ats-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>N.° Nota Crédito</th>
                  <th>Doc. Modificado</th>
                  <th>Identificación</th>
                  <th>Razón Social</th>
                  <th>Motivo</th>
                  <th class="text-end">Base Imponible</th>
                  <th class="text-end">IVA</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!reporte.notas_credito.length">
                  <td colspan="10" class="ats-vacio">Sin notas de crédito en este período</td>
                </tr>
                <tr v-for="(nc, i) in reporte.notas_credito" :key="nc.id">
                  <td class="ats-num-col">{{ i + 1 }}</td>
                  <td>{{ nc.fecha_emision }}</td>
                  <td class="ats-mono">{{ nc.numero_comprobante }}</td>
                  <td class="ats-mono">{{ nc.numero_doc_modificado }}</td>
                  <td>{{ nc.identificacion }}</td>
                  <td>{{ nc.razon_social }}</td>
                  <td class="ats-motivo">{{ nc.motivo }}</td>
                  <td class="text-end">{{ fmtMon(nc.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(nc.iva_nota) }}</td>
                  <td class="text-end fw-semibold">{{ fmtMon(nc.total) }}</td>
                </tr>
              </tbody>
              <tfoot v-if="reporte.notas_credito.length">
                <tr>
                  <td colspan="7">Total ({{ reporte.resumen.notas_credito.cantidad }} notas)</td>
                  <td class="text-end">{{ fmtMon(reporte.resumen.notas_credito.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.notas_credito.iva) }}</td>
                  <td class="text-end fw-bold">{{ fmtMon(reporte.resumen.notas_credito.total) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- ── Anuladas ───────────────────────────────────────────── -->
          <div v-show="tabActivo === 'anuladas'" class="table-responsive">
            <table class="ats-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>N.° Comprobante</th>
                  <th>Identificación</th>
                  <th>Razón Social</th>
                  <th class="text-end">Base Imponible</th>
                  <th class="text-end">IVA</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!reporte.anuladas.length">
                  <td colspan="8" class="ats-vacio">Sin documentos anulados en este período</td>
                </tr>
                <tr v-for="(a, i) in reporte.anuladas" :key="a.id">
                  <td class="ats-num-col">{{ i + 1 }}</td>
                  <td>{{ a.fecha_emision }}</td>
                  <td class="ats-mono">{{ a.numero_comprobante }}</td>
                  <td>{{ a.identificacion }}</td>
                  <td>{{ a.razon_social }}</td>
                  <td class="text-end">{{ fmtMon(a.base_imponible) }}</td>
                  <td class="text-end" style="color:var(--accent-warning)">{{ fmtMon(a.iva) }}</td>
                  <td class="text-end fw-semibold">{{ fmtMon(a.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div><!-- /panel__cuerpo -->
      </div><!-- /ats-panel -->

      <!-- ── Cuadro resumen IVA ─────────────────────────────────────── -->
      <div class="row g-3 mt-3">
        <div class="col-md-6">
          <div class="ats-panel">
            <div class="ats-panel__cabecera">
              <span class="fw-semibold">Resumen IVA del período</span>
            </div>
            <div class="ats-panel__cuerpo p-0">
              <table class="ats-tabla">
                <tbody>
                  <tr>
                    <td>IVA en ventas (cobrado)</td>
                    <td class="text-end fw-semibold" style="color:var(--accent-success)">{{ fmtMon(reporte.resumen.iva_ventas) }}</td>
                  </tr>
                  <tr>
                    <td>IVA en compras (pagado)</td>
                    <td class="text-end fw-semibold" style="color:var(--accent-warning)">{{ fmtMon(reporte.resumen.iva_compras) }}</td>
                  </tr>
                  <tr class="ats-fila-total">
                    <!-- IVA por pagar si ventas > compras; Crédito tributario si compras > ventas -->
                    <template v-if="reporte.resumen.diferencia_iva > 0">
                      <td class="fw-bold" style="color:var(--accent-danger)">
                        <i class="bi bi-exclamation-circle-fill me-1"></i>IVA por pagar
                      </td>
                      <td class="text-end fw-bold" style="color:var(--accent-danger)">
                        {{ fmtMon(reporte.resumen.diferencia_iva) }}
                      </td>
                    </template>
                    <template v-else-if="reporte.resumen.diferencia_iva < 0">
                      <td class="fw-bold" style="color:var(--accent-success)">
                        <i class="bi bi-check-circle-fill me-1"></i>Crédito tributario
                      </td>
                      <td class="text-end fw-bold" style="color:var(--accent-success)">
                        {{ fmtMon(Math.abs(reporte.resumen.diferencia_iva)) }}
                      </td>
                    </template>
                    <template v-else>
                      <td class="fw-bold">Sin diferencia de IVA</td>
                      <td class="text-end fw-bold">{{ fmtMon(0) }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="ats-panel">
            <div class="ats-panel__cabecera">
              <span class="fw-semibold">Resumen de bases imponibles</span>
            </div>
            <div class="ats-panel__cuerpo p-0">
              <table class="ats-tabla">
                <tbody>
                  <tr>
                    <td>Base imponible ventas</td>
                    <td class="text-end fw-semibold">{{ fmtMon(reporte.resumen.ventas.base_imponible) }}</td>
                  </tr>
                  <tr>
                    <td>Base imponible compras</td>
                    <td class="text-end fw-semibold">{{ fmtMon(reporte.resumen.compras.base_imponible) }}</td>
                  </tr>
                  <tr>
                    <td>Base notas de crédito</td>
                    <td class="text-end fw-semibold">{{ fmtMon(reporte.resumen.notas_credito.base_imponible) }}</td>
                  </tr>
                  <tr class="ats-fila-total">
                    <td class="fw-bold">Docs. anulados</td>
                    <td class="text-end fw-bold">{{ reporte.resumen.anuladas.cantidad }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </template><!-- /v-if reporte -->

    <!-- Estado vacío (sin consultar) -->
    <div v-if="!reporte && !cargando && !error" class="ats-estado-vacio">
      <i class="bi bi-file-earmark-ruled fs-2 mb-2 d-block"></i>
      Seleccioná el período y presioná <strong>Consultar</strong> para generar el ATS.
    </div>

  </div>
</template>

<script>
import api from '@/services/api'
import * as XLSX from 'xlsx'

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

const FORMAS_PAGO = {
  '01': 'Efectivo',
  '15': 'Compensac.',
  '16': 'Tarjeta déb.',
  '17': 'Tarjeta créd.',
  '18': 'Dinero electr.',
  '19': 'Tarjeta prepago',
  '20': 'Transferencia',
  '21': 'Cheque',
}

export default {
  name: 'ClientReportesAts',

  data() {
    const ahora = new Date()
    return {
      filtro: {
        mes:  ahora.getMonth() + 1,
        anio: ahora.getFullYear(),
      },
      reporte:   null,
      cargando:  false,
      exportando: false,
      error:     null,
      tabActivo: 'ventas',
      mesesNombres: MESES,
    }
  },

  computed: {
    aniosDisponibles() {
      const anio = new Date().getFullYear()
      const lista = []
      for (let a = anio; a >= 2020; a--) lista.push(a)
      return lista
    },

    pestanas() {
      if (!this.reporte) return []
      return [
        { id: 'ventas',        icono: 'bi-receipt',           etiqueta: 'Ventas',          conteo: this.reporte.ventas.length },
        { id: 'compras',       icono: 'bi-cart3',             etiqueta: 'Compras',         conteo: this.reporte.compras.length },
        { id: 'notas_credito', icono: 'bi-file-minus',        etiqueta: 'Notas de crédito', conteo: this.reporte.notas_credito.length },
        { id: 'anuladas',      icono: 'bi-x-circle',          etiqueta: 'Anuladas',        conteo: this.reporte.anuladas.length },
      ]
    },
  },

  mounted() {
    this.cargar()
  },

  methods: {
    nombreMes(n) {
      return MESES[(n || 1) - 1] || ''
    },

    fmtMon(v) {
      if (v === null || v === undefined) return '$0.00'
      return '$' + Number(v).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    fmtFormaPago(codigo) {
      return FORMAS_PAGO[String(codigo)] || codigo || '—'
    },

    async cargar() {
      this.cargando = true
      this.error    = null
      this.reporte  = null
      try {
        const { data } = await api.get('/reportes/ats', {
          params: { anio: this.filtro.anio, mes: this.filtro.mes },
        })
        if (data.exito) {
          this.reporte  = data
          this.tabActivo = 'ventas'
        } else {
          this.error = data.error || 'No se pudo cargar el ATS'
        }
      } catch (e) {
        this.error = e?.response?.data?.error || 'Error al conectar con el servidor'
      } finally {
        this.cargando = false
      }
    },

    exportarXlsx() {
      if (!this.reporte) return
      this.exportando = true
      try {
        const r = this.reporte
        const periodo = `${this.nombreMes(r.periodo.mes)} ${r.periodo.anio}`
        const wb = XLSX.utils.book_new()

        // ── Hoja 1: Resumen ───────────────────────────────────────────
        const resumenData = [
          ['ATS — Anexo Transaccional Simplificado'],
          [`Empresa: ${r.empresa.razon_social}`, `RUC: ${r.empresa.ruc}`],
          [`Período: ${periodo}`],
          [],
          ['Sección', 'Cantidad', 'Base Imponible', 'IVA', 'Total'],
          ['Ventas',
            r.resumen.ventas.cantidad,
            r.resumen.ventas.base_imponible,
            r.resumen.iva_ventas,
            r.resumen.ventas.total],
          ['Compras',
            r.resumen.compras.cantidad,
            r.resumen.compras.base_imponible,
            r.resumen.iva_compras,
            r.resumen.compras.total],
          ['Notas de crédito',
            r.resumen.notas_credito.cantidad,
            r.resumen.notas_credito.base_imponible,
            r.resumen.notas_credito.iva,
            r.resumen.notas_credito.total],
          ['Documentos anulados', r.resumen.anuladas.cantidad, '', '', ''],
          [],
          ['IVA ventas (cobrado)',   '', '', r.resumen.iva_ventas,   ''],
          ['IVA compras (pagado)',   '', '', r.resumen.iva_compras,  ''],
          ['IVA por pagar',         '', '', r.resumen.diferencia_iva > 0 ? r.resumen.diferencia_iva : 0, ''],
          ['Crédito tributario',    '', '', r.resumen.diferencia_iva < 0 ? Math.abs(r.resumen.diferencia_iva) : 0, ''],
        ]
        const wsRes = XLSX.utils.aoa_to_sheet(resumenData)
        XLSX.utils.book_append_sheet(wb, wsRes, 'Resumen')

        // ── Hoja 2: Ventas ─────────────────────────────────────────────
        const cabVentas = ['#', 'Fecha', 'N.° Comprobante', 'Clave Acceso', 'N.° Autorización',
          'Tipo Comprobante', 'Forma Pago', 'Tipo Identificación', 'Identificación',
          'Razón Social', 'Base Imponible', 'IVA Cobrado', 'Descuento', 'Total']
        const filasVentas = r.ventas.map((f, i) => [
          i + 1, f.fecha_emision, f.numero_comprobante, f.clave_acceso || '', f.numero_autorizacion || '',
          f.tipo_comprobante, this.fmtFormaPago(f.forma_pago), f.tipo_identificacion,
          f.identificacion, f.razon_social,
          parseFloat(f.base_imponible), parseFloat(f.iva_cobrado), parseFloat(f.descuento), parseFloat(f.total),
        ])
        filasVentas.push(['', '', '', '', '', '', '', '', '', 'TOTAL',
          r.resumen.ventas.base_imponible, r.resumen.iva_ventas, '', r.resumen.ventas.total])
        const wsVentas = XLSX.utils.aoa_to_sheet([cabVentas, ...filasVentas])
        XLSX.utils.book_append_sheet(wb, wsVentas, 'Ventas')

        // ── Hoja 3: Compras ────────────────────────────────────────────
        const cabCompras = ['#', 'Fecha', 'N.° Factura Proveedor', 'Clave Acceso SRI',
          'Tipo Identificación', 'Identificación', 'Razón Social',
          'Base Imponible', 'IVA Pagado', 'Descuento', 'Total']
        const filasCompras = r.compras.map((c, i) => [
          i + 1, c.fecha_emision, c.numero_factura, c.clave_acceso || '',
          c.tipo_identificacion, c.identificacion, c.razon_social,
          parseFloat(c.base_imponible), parseFloat(c.iva_pagado), parseFloat(c.descuento), parseFloat(c.total),
        ])
        filasCompras.push(['', '', '', '', '', '', 'TOTAL',
          r.resumen.compras.base_imponible, r.resumen.iva_compras, '', r.resumen.compras.total])
        const wsCompras = XLSX.utils.aoa_to_sheet([cabCompras, ...filasCompras])
        XLSX.utils.book_append_sheet(wb, wsCompras, 'Compras')

        // ── Hoja 4: Notas de crédito ───────────────────────────────────
        const cabNC = ['#', 'Fecha', 'N.° Nota Crédito', 'Clave Acceso', 'N.° Autorización',
          'Doc. Modificado', 'Fecha Doc. Modificado', 'Motivo',
          'Tipo Identificación', 'Identificación', 'Razón Social',
          'Base Imponible', 'IVA', 'Descuento', 'Total']
        const filasNC = r.notas_credito.map((nc, i) => [
          i + 1, nc.fecha_emision, nc.numero_comprobante, nc.clave_acceso || '', nc.numero_autorizacion || '',
          nc.numero_doc_modificado, nc.fecha_emision_doc_modificado, nc.motivo,
          nc.tipo_identificacion, nc.identificacion, nc.razon_social,
          parseFloat(nc.base_imponible), parseFloat(nc.iva_nota), parseFloat(nc.descuento), parseFloat(nc.total),
        ])
        const wsNC = XLSX.utils.aoa_to_sheet([cabNC, ...filasNC])
        XLSX.utils.book_append_sheet(wb, wsNC, 'Notas de Crédito')

        // ── Hoja 5: Anuladas ───────────────────────────────────────────
        const cabAnuladas = ['#', 'Fecha', 'N.° Comprobante', 'Clave Acceso',
          'Tipo Comprobante', 'Identificación', 'Razón Social',
          'Base Imponible', 'IVA', 'Total']
        const filasAnuladas = r.anuladas.map((a, i) => [
          i + 1, a.fecha_emision, a.numero_comprobante, a.clave_acceso || '',
          a.tipo_comprobante, a.identificacion, a.razon_social,
          parseFloat(a.base_imponible), parseFloat(a.iva), parseFloat(a.total),
        ])
        const wsAnuladas = XLSX.utils.aoa_to_sheet([cabAnuladas, ...filasAnuladas])
        XLSX.utils.book_append_sheet(wb, wsAnuladas, 'Anuladas')

        const nombreArchivo = `ATS_${r.empresa.ruc}_${String(r.periodo.mes).padStart(2,'0')}_${r.periodo.anio}.xlsx`
        XLSX.writeFile(wb, nombreArchivo)
      } finally {
        this.exportando = false
      }
    },
  },
}
</script>

<style scoped>
.ats-reporte {
  padding: 1rem 1.25rem;
  color: var(--text-primary);
}

.ats-titulo {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
}

.ats-periodo-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: var(--primary-main);
  color: #fff;
}

/* ── Panel ──────────────────────────────────────────────────────────────── */
.ats-panel {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
}

.ats-panel__cabecera {
  padding: 0.6rem 1rem;
  border-bottom: 1px solid var(--border-main);
  background: var(--secondary-bg-main);
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.ats-panel__cuerpo {
  padding: 0.75rem 1rem;
}

/* ── KPI ────────────────────────────────────────────────────────────────── */
.ats-kpi {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: 8px;
  padding: 0.85rem 1rem;
  text-align: center;
  box-shadow: var(--shadow-sm);
}

.ats-kpi__valor {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.ats-kpi__etiqueta {
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-top: 3px;
}

/* ── Pestañas ───────────────────────────────────────────────────────────── */
.ats-tab {
  padding: 0.5rem 1rem;
  font-size: 0.82rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
}

.ats-tab:hover {
  color: var(--primary-main);
}

.ats-tab--activo {
  color: var(--primary-main);
  border-bottom-color: var(--primary-main);
  font-weight: 600;
}

.ats-tab__conteo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: var(--border-main);
  color: var(--text-secondary);
  font-size: 0.68rem;
  font-weight: 700;
  margin-left: 5px;
  padding: 0 4px;
}

.ats-tab--activo .ats-tab__conteo {
  background: var(--primary-main);
  color: #fff;
}

/* ── Tabla ──────────────────────────────────────────────────────────────── */
.ats-tabla {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  color: var(--text-primary);
}

.ats-tabla thead tr {
  background: var(--secondary-bg-main);
  border-bottom: 2px solid var(--border-main);
}

.ats-tabla th {
  padding: 0.5rem 0.6rem;
  font-size: 0.73rem;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
}

.ats-tabla td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid var(--border-main);
}

.ats-tabla tbody tr:hover td {
  background: var(--table-hover, rgba(0,0,0,.03));
}

.ats-tabla tfoot td {
  font-weight: 700;
  background: var(--secondary-bg-main);
  border-top: 2px solid var(--border-main);
  padding: 0.45rem 0.6rem;
}

.ats-fila-total td {
  background: var(--secondary-bg-main);
  border-top: 2px solid var(--border-main);
  font-size: 0.82rem;
}

.ats-num-col   { width: 36px; text-align: center; color: var(--text-muted); }
.ats-mono      { font-family: monospace; font-size: 0.75rem; }
.ats-motivo    { max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ats-vacio {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-muted);
  font-size: 0.85rem;
}

/* ── Alerta ─────────────────────────────────────────────────────────────── */
.ats-alerta {
  padding: 0.65rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
}

.ats-alerta--error {
  background: rgba(239,68,68,.08);
  color: var(--accent-danger);
  border: 1px solid rgba(239,68,68,.2);
}

/* ── Estado vacío ───────────────────────────────────────────────────────── */
.ats-estado-vacio {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.9rem;
}

/* ── Input ──────────────────────────────────────────────────────────────── */
.ats-input {
  background: var(--secondary-bg-main);
  border-color: var(--border-main);
  color: var(--text-primary);
}

.ats-input:focus {
  border-color: var(--border-focus);
  box-shadow: 0 0 0 2px rgba(14,165,233,.15);
  background: var(--secondary-bg-main);
  color: var(--text-primary);
}
</style>

